import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import rankingService, { RecentRankingItem, formatRankingDate } from '../../services/rankingService';
import infoPageService from '../../services/infoPageService';
import { t } from '../../i18n';

// ============================================
// „Aktuelle Ranglisten“ – horizontale Scroll-Sektion auf der Startseite
// (Marcel 2026-10-20): zuletzt aktualisierte Listing-Ranglisten (Ligen,
// Vereine, Verbände, Spielstätten) als Karten im Stil von „Darts-Wissen“.
// Karten sind ECHTE Links (<a href>) → SEO: die Ranglisten-Seiten hängen
// direkt an der Startseite. Bewusst OHNE „Alle“-Link (Marcel 2026-10-20:
// /ranglisten sind die Profi-Ranglisten). Blendet sich bei leeren Daten aus.
// ============================================

const ACCENT = '#3fced1';
const MAX_ITEMS = 12;

const typeLabel = (type: string): string => {
  switch (type) {
    case 'club': return t('web.component.home-recent-rankings.typ-club');
    case 'league': return t('web.component.home-recent-rankings.typ-league');
    case 'association': return t('web.component.home-recent-rankings.typ-association');
    case 'pub': return t('web.component.home-recent-rankings.typ-pub');
    default: return type;
  }
};

const HomeRecentRankings: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<RecentRankingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    rankingService
      .recent(MAX_ITEMS)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch(() => { /* Section bleibt ausgeblendet */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });

  if (!loading && items.length === 0) return null;

  return (
    <section aria-label={t('web.component.home-recent-rankings.titel')} data-testid="home-recent-rankings">
      <div className="mb-3 flex items-center justify-between">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-recent-rankings.titel')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.home-recent-rankings.untertitel')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {!loading && items.length > 3 && (
            <>
              <button type="button" aria-label={t('web.component.home-recent-rankings.zurueck')} onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex">
                <ChevronLeft size={15} />
              </button>
              <button type="button" aria-label={t('web.component.home-recent-rankings.weiter')} onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex">
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[200px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900" />
            ))
          : items.map((item) => <RankingCard key={item.id} item={item} />)}
      </div>
    </section>
  );
};

const RankingCard: React.FC<{ item: RecentRankingItem }> = ({ item }) => {
  const [imgError, setImgError] = useState(false);
  const image = infoPageService.buildFileUrl(item.listing.image);
  const showImage = image && !imgError;
  return (
    <Link
      to={item.path}
      data-testid="home-recent-ranking-card"
      className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="flex items-center gap-2.5 px-3 pt-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-800">
          {showImage ? (
            <img src={image as string} alt="" loading="lazy" onError={() => setImgError(true)} className="h-full w-full object-cover" />
          ) : (
            <Trophy size={16} style={{ color: ACCENT }} />
          )}
        </div>
        <div className="min-w-0">
          <span className="block truncate text-[12px] font-semibold text-gray-300">{item.listing.name || typeLabel(item.listing.type)}</span>
          <span className="block text-[10.5px] font-bold uppercase tracking-wide" style={{ color: ACCENT }}>{typeLabel(item.listing.type)}</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col px-3 pb-3 pt-2.5">
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {item.title}{item.season ? ` · ${item.season}` : ''}
        </h3>
        {item.top.length > 0 && (
          <ol className="mt-2 space-y-0.5">
            {item.top.map((e) => (
              <li key={`${e.rank}-${e.playerName}`} className="flex items-center gap-2 text-[11.5px] text-gray-400">
                <span className="w-4 shrink-0 font-bold text-gray-500">{e.rank ?? '–'}.</span>
                <span className="min-w-0 flex-1 truncate text-gray-300">{e.playerName}</span>
                {e.points !== null && <span className="shrink-0 font-semibold" style={{ color: ACCENT }}>{e.points}</span>}
              </li>
            ))}
          </ol>
        )}
        <p className="mt-auto pt-2 text-[11px] text-gray-500">
          {t('web.component.home-recent-rankings.spieler', { count: item.entryCount })}
          {item.lastUpdated > 0 ? ` · ${t('web.component.home-recent-rankings.aktualisiert', { date: formatRankingDate(item.lastUpdated) })}` : ''}
        </p>
      </div>
    </Link>
  );
};

export default HomeRecentRankings;
